import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, CalendarDays, Clock3, MapPin, Users, Plus, Trash2, CheckCircle2, AlertCircle, MessageCircle, CreditCard, Send, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const LEVELS = [
  { v: "Beginner", es: "Principiante" },
  { v: "Intermediate", es: "Intermedio" },
  { v: "Advanced", es: "Avanzado" },
  { v: "Not sure", es: "No estoy seguro/a" },
];
const IMPROVE = [
  { v: "Speaking confidence", es: "Confianza al hablar" },
  { v: "Pronunciation", es: "Pronunciación" },
  { v: "Vocabulary", es: "Vocabulario" },
  { v: "Listening comprehension", es: "Comprensión auditiva" },
  { v: "Everyday conversations", es: "Conversaciones cotidianas" },
  { v: "Spanish for work", es: "Español para el trabajo" },
  { v: "Spanish for travel", es: "Español para viajar" },
  { v: "Speaking more naturally", es: "Hablar con más naturalidad" },
  { v: "Other", es: "Otro" },
];
const STUDIED = [{ v: "Yes", es: "Sí" }, { v: "No", es: "No" }, { v: "A little", es: "Un poco" }];
const AGE_CAT = [{ v: "Adult", es: "Adulto/a" }, { v: "Minor", es: "Menor de edad" }];

const POLICIES = {
  en: [
    "Registration is required for each Thursday you plan to attend.",
    "You may register and pay for multiple upcoming sessions in advance.",
    "Each session costs $20 per person.",
    "Space is limited, and registration is subject to availability.",
    "Registration is confirmed once payment has been received and verified.",
    "Please contact Alma Y Lengua if you cannot attend a session.",
    "Payment for a session does not automatically enroll you in future weeks.",
  ],
  es: [
    "Es necesario registrarse para cada jueves que planeas asistir.",
    "Puedes registrarte y pagar por varias sesiones futuras con anticipación.",
    "Cada sesión cuesta $20 por persona.",
    "Los espacios son limitados y la inscripción está sujeta a disponibilidad.",
    "Tu inscripción queda confirmada una vez recibido y verificado el pago.",
    "Por favor, comunícate con Alma Y Lengua si no puedes asistir.",
    "El pago de una sesión no te inscribe automáticamente en las siguientes semanas.",
  ],
};

const T = {
  en: {
    switch: "Español",
    kicker: "Weekly Program",
    title: "Spanish Conversation Club",
    sub: "Practice your Spanish in a welcoming, interactive environment! Our weekly conversation club is an opportunity to build confidence, improve communication skills, and practice speaking Spanish with others.",
    sub2: "Whether you are a beginner, intermediate, or advanced learner, we would love to learn more about your experience and conversational goals.",
    limited: "Space is limited. Advance registration is required for each session.",
    perSession: "per person, per session",
    format: "In-person, small-group conversational Spanish practice",
    step1: "1. Choose Your Thursdays",
    step1Hint: "Select one or more upcoming sessions. All participants on this registration attend the same dates — for different dates, please submit a separate registration.",
    full: "Full", closed: "Closed", cutoff: "Registration closed", cancelled: "Cancelled",
    spotsLeft: (n) => `${n} ${n === 1 ? "spot" : "spots"} left`,
    step2: "2. Your Information",
    name: "Full Name", phone: "Phone Number", email: "Email Address",
    level: "How would you describe your current Spanish level?",
    knowledge: "How much Spanish do you currently know?",
    knowledgeHint: "Optional",
    goals: "What are your goals for practicing conversational Spanish?",
    improve: "What would you most like to improve?",
    improveHint: "Select all that apply",
    studied: "Have you studied Spanish before?",
    notes: "Is there anything else you would like us to know?",
    notesHint: "Optional",
    step3: "3. Additional Participants",
    addPersonQ: "Would you like to register another person?",
    yes: "Yes", no: "No",
    addParticipant: "Add Participant",
    participant: "Participant",
    remove: "Remove",
    pName: "Full name", pLevel: "Spanish level", pGoals: "Conversational Spanish goals", pAge: "Age category", pEmail: "Email (optional)", pPhone: "Phone number (optional)",
    step4: "4. Price Summary & Payment",
    summarySessions: "Selected sessions", summaryPeople: "Participants", summaryPrice: "Price per person / session", summaryTotal: "Total",
    payTitle: "Payment Method",
    payVenmo: "Venmo", payOnline: "Pay Online",
    policiesTitle: "Registration & Payment Policies",
    agree: "I understand and agree to the registration and payment policies.",
    submit: "Submit Registration", submitting: "Submitting...",
    errRequired: "Please complete all required fields (*), select at least one Thursday, choose a payment method, and agree to the policies.",
    errGeneric: "Something went wrong. Please try again.",
    confirmPendingTitle: "Registration Received — Pending Payment",
    confirmBody: "Your seats are temporarily reserved. Your registration is NOT confirmed until your payment has been received and verified.",
    regNumber: "Registration number", primaryName: "Primary registrant", additionalNames: "Additional participants",
    dates: "Selected dates", seats: "Reserved seats", total: "Total amount", method: "Payment method",
    payStatus: "Payment status", regStatus: "Registration status",
    pendingVerification: "Pending Verification", pendingPayment: "Pending Payment",
    venmoHow: "How to pay with Venmo",
    payNow: "Pay with PayPal",
    holdNote: (h) => `Seats are held for ${h} hours. Unverified reservations are released after that time.`,
    returnHome: "Return to Home Page",
    none: "None",
  },
  es: {
    switch: "English",
    kicker: "Programa Semanal",
    title: "Club de Conversación en Español",
    sub: "¡Practica tu español en un ambiente acogedor e interactivo! Nuestro club de conversación semanal es una oportunidad para desarrollar confianza, mejorar tus habilidades de comunicación y practicar español con otras personas.",
    sub2: "Ya seas principiante, intermedio o avanzado, nos encantaría conocer tu experiencia y tus metas de conversación.",
    limited: "Los espacios son limitados. Es necesario registrarse con anticipación para cada sesión.",
    perSession: "por persona, por sesión",
    format: "Práctica de español conversacional en persona, en grupo pequeño",
    step1: "1. Elige Tus Jueves",
    step1Hint: "Selecciona una o más sesiones próximas. Todos los participantes de esta inscripción asisten a las mismas fechas — para fechas diferentes, envía una inscripción separada.",
    full: "Lleno", closed: "Cerrado", cutoff: "Registro cerrado", cancelled: "Cancelada",
    spotsLeft: (n) => `${n} ${n === 1 ? "lugar disponible" : "lugares disponibles"}`,
    step2: "2. Tu Información",
    name: "Nombre completo", phone: "Número de teléfono", email: "Correo electrónico",
    level: "¿Cómo describirías tu nivel actual de español?",
    knowledge: "¿Cuánto español sabes actualmente?",
    knowledgeHint: "Opcional",
    goals: "¿Cuáles son tus metas al practicar español conversacional?",
    improve: "¿Qué te gustaría mejorar más?",
    improveHint: "Selecciona todas las que apliquen",
    studied: "¿Has estudiado español anteriormente?",
    notes: "¿Hay algo más que te gustaría que supiéramos?",
    notesHint: "Opcional",
    step3: "3. Participantes Adicionales",
    addPersonQ: "¿Te gustaría registrar a otra persona?",
    yes: "Sí", no: "No",
    addParticipant: "Agregar participante",
    participant: "Participante",
    remove: "Eliminar",
    pName: "Nombre completo", pLevel: "Nivel de español", pGoals: "Metas de conversación en español", pAge: "Categoría de edad", pEmail: "Correo (opcional)", pPhone: "Teléfono (opcional)",
    step4: "4. Resumen de Precio y Pago",
    summarySessions: "Sesiones seleccionadas", summaryPeople: "Participantes", summaryPrice: "Precio por persona / sesión", summaryTotal: "Total",
    payTitle: "Método de Pago",
    payVenmo: "Venmo", payOnline: "Pagar en línea",
    policiesTitle: "Políticas de Inscripción y Pago",
    agree: "Entiendo y acepto las políticas de inscripción y pago.",
    submit: "Enviar Inscripción", submitting: "Enviando...",
    errRequired: "Por favor completa todos los campos requeridos (*), selecciona al menos un jueves, elige un método de pago y acepta las políticas.",
    errGeneric: "Algo salió mal. Inténtalo de nuevo.",
    confirmPendingTitle: "Inscripción Recibida — Pago Pendiente",
    confirmBody: "Tus lugares están reservados temporalmente. Tu inscripción NO está confirmada hasta que tu pago haya sido recibido y verificado.",
    regNumber: "Número de inscripción", primaryName: "Registrante principal", additionalNames: "Participantes adicionales",
    dates: "Fechas seleccionadas", seats: "Lugares reservados", total: "Monto total", method: "Método de pago",
    payStatus: "Estado del pago", regStatus: "Estado de la inscripción",
    pendingVerification: "Pendiente de verificación", pendingPayment: "Pago pendiente",
    venmoHow: "Cómo pagar con Venmo",
    payNow: "Pagar con PayPal",
    holdNote: (h) => `Los lugares se reservan por ${h} horas. Las reservas no verificadas se liberan después de ese tiempo.`,
    returnHome: "Volver a la Página Principal",
    none: "Ninguno",
  },
};

const inp = "w-full bg-white rounded-xl border border-slate-300 px-4 py-3 text-[16px] outline-none transition focus:ring-2 focus:ring-brand focus:border-transparent";
const Lbl = ({ children, req, hint }) => (
  <label className="block text-sm font-semibold text-navy mb-2">{children}{req && <span className="text-red-500"> *</span>}{hint && <span className="block text-xs font-normal text-ink/50 mt-0.5">{hint}</span>}</label>
);
const fmtDate = (iso, lang) => {
  try { return new Date(iso + "T12:00:00").toLocaleDateString(lang === "es" ? "es" : "en-US", { weekday: "long", month: "long", day: "numeric" }); } catch { return iso; }
};
const emptyExtra = { name: "", spanish_level: "", goals: "", age_category: "", email: "", phone: "" };
const emptyData = {
  full_name: "", phone: "", email: "", spanish_level: "", spanish_knowledge: "", goals: "",
  improve: [], studied_before: "", notes: "", session_dates: [], payment_method: "", policies_ack: false,
};

export default function Club() {
  const [lang, setLang] = useState("en");
  const tr = T[lang];
  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [d, setD] = useState(emptyData);
  const [addPerson, setAddPerson] = useState("");
  const [extras, setExtras] = useState([]);
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { document.title = "Spanish Conversation Club | Alma y Lengua"; }, []);
  useEffect(() => {
    api.get("/club/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/club/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, []);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setExtra = (i, k, v) => setExtras((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const toggleDate = (dt) => setD((p) => ({ ...p, session_dates: p.session_dates.includes(dt) ? p.session_dates.filter((x) => x !== dt) : [...p.session_dates, dt].sort() }));
  const toggleImprove = (v) => setD((p) => ({ ...p, improve: p.improve.includes(v) ? p.improve.filter((x) => x !== v) : [...p.improve, v] }));

  const price = config?.price_per_person ?? 20;
  const effectiveExtras = addPerson === "yes" ? extras : [];
  const seats = 1 + effectiveExtras.length;
  const total = d.session_dates.length * seats * price;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
  const extrasValid = effectiveExtras.every((x) => x.name.trim() && x.spanish_level && x.goals.trim() && x.age_category);
  const valid = d.full_name.trim() && d.phone.trim() && emailValid && d.spanish_level && d.goals.trim() &&
    d.studied_before && d.session_dates.length > 0 && d.payment_method && d.policies_ack &&
    (addPerson !== "yes" || (effectiveExtras.length > 0 && extrasValid)) && addPerson !== "";

  const submit = async () => {
    if (!valid) { setErr(tr.errRequired); document.getElementById("club-form")?.scrollIntoView({ behavior: "smooth" }); return; }
    setStatus("sending"); setErr("");
    try {
      const { data } = await api.post("/club/registrations", { ...d, additional_participants: effectiveExtras });
      setResult(data); setStatus("success"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setStatus("error");
      setErr(e?.response?.data?.detail || tr.errGeneric);
      document.getElementById("club-form")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (status === "success" && result) {
    const extraNames = (result.additional_participants || []).map((p) => p.name);
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16 bg-offwhite grain">
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "backOut" }}
          className="max-w-xl w-full rounded-3xl shadow-2xl p-8 md:p-10 bg-white border border-navy/5" data-testid="club-confirmation">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring" }}
            className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6 bg-gold text-navy"><MessageCircle size={40} /></motion.div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-center mb-2 text-navy" data-testid="club-confirmation-title">{tr.confirmPendingTitle}</h1>
          <p className="text-center text-ink/70 mb-6">{tr.confirmBody}</p>
          <div className="rounded-2xl bg-offwhite p-5 mb-5 text-sm space-y-2" data-testid="club-confirmation-details">
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.regNumber}</span><span className="font-extrabold text-brand" data-testid="club-reg-number">{result.registration_number}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.primaryName}</span><span>{result.full_name}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.additionalNames}</span><span className="text-right">{extraNames.length ? extraNames.join(", ") : tr.none}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.dates}</span><span className="text-right">{(result.session_dates || []).map((x) => fmtDate(x, lang)).join("; ")}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.seats}</span><span>{result.seats}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.total}</span><span className="font-extrabold" data-testid="club-confirmation-total">${result.total_amount}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.method}</span><span>{result.payment_method === "venmo" ? "Venmo" : tr.payOnline}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.payStatus}</span><span className="font-bold text-orange-600" data-testid="club-payment-status">{tr.pendingVerification}</span></div>
            <div className="flex justify-between gap-3"><span className="font-semibold text-navy">{tr.regStatus}</span><span className="font-bold text-orange-600">{tr.pendingPayment}</span></div>
          </div>
          <div className="rounded-2xl border-2 border-gold/60 bg-gold/10 p-5 mb-5" data-testid="club-payment-instructions">
            <p className="font-extrabold text-navy mb-2">{result.payment_method === "venmo" ? tr.venmoHow : tr.payOnline}</p>
            <p className="text-sm text-ink/80 mb-2">{lang === "es" ? (result.payment_method === "venmo" ? result.payment_instructions_es : result.online_instructions_es) : (result.payment_method === "venmo" ? result.payment_instructions_en : result.online_instructions_en)}</p>
            {result.payment_method === "online" && result.payment_link && (
              <a href={result.payment_link} target="_blank" rel="noopener noreferrer" data-testid="club-pay-now"
                className="mt-2 inline-flex items-center gap-2 rounded-full px-6 py-3 font-extrabold text-white bg-brand hover:bg-navy transition-colors">
                <CreditCard size={18} /> {tr.payNow} — ${result.total_amount}
              </a>
            )}
            {result.pending_expiration_hours > 0 && <p className="text-xs text-ink/50 mt-3">{tr.holdNote(result.pending_expiration_hours)}</p>}
          </div>
          <div className="text-center">
            <Link to="/" data-testid="club-return-home" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white bg-navy hover:bg-brand transition-colors"><Home size={18} /> {tr.returnHome}</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const stateBadge = (s) => {
    if (s.state === "full") return <span className="text-[11px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1 bg-red-100 text-red-600">{tr.full}</span>;
    if (s.state === "closed") return <span className="text-[11px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1 bg-navy/10 text-navy">{tr.closed}</span>;
    if (s.state === "cutoff") return <span className="text-[11px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1 bg-navy/10 text-navy">{tr.cutoff}</span>;
    if (s.state === "cancelled") return <span className="text-[11px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1 bg-red-100 text-red-600">{tr.cancelled}</span>;
    if (s.remaining_seats !== null && s.remaining_seats !== undefined) return <span className="text-[11px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1 bg-aqua text-brand">{tr.spotsLeft(s.remaining_seats)}</span>;
    return null;
  };

  return (
    <div className="min-h-screen bg-offwhite grain">
      {/* Lang toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button onClick={() => setLang(lang === "en" ? "es" : "en")} data-testid="club-lang" className="rounded-full px-4 py-2 text-sm font-bold text-white shadow-lg bg-navy">{tr.switch}</button>
      </div>

      {/* Hero */}
      <section className="bg-navy text-white grain">
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6 bg-gold text-navy">{tr.kicker}</span>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold leading-tight mb-5" data-testid="club-title">{tr.title}</h1>
            <p className="text-lg text-white/85 max-w-2xl mx-auto mb-3">{tr.sub}</p>
            <p className="text-white/70 max-w-2xl mx-auto">{tr.sub2}</p>
          </motion.div>
        </div>
      </section>

      {/* Info card */}
      <section className="max-w-3xl mx-auto px-5 -mt-8 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl border border-navy/5 p-6 grid sm:grid-cols-2 gap-4" data-testid="club-info-card">
          {[
            { Icon: CalendarDays, t: config ? (lang === "es" ? `${config.day_label_es}s` : `${config.day_label_en}s`) : "…" },
            { Icon: Clock3, t: `${config?.time_label || "6:30–7:30 PM"} ET` },
            { Icon: MapPin, t: config?.location || "24 S 3rd St, Unit 105, Oxford, PA" },
            { Icon: Users, t: `$${price} ${tr.perSession}` },
          ].map(({ Icon, t }) => (
            <div key={t} className="flex items-center gap-3">
              <span className="w-10 h-10 shrink-0 rounded-2xl grid place-items-center bg-aqua text-brand"><Icon size={18} /></span>
              <span className="text-sm font-bold text-navy leading-snug">{t}</span>
            </div>
          ))}
          <p className="sm:col-span-2 text-sm text-ink/60">{tr.format} · <strong className="text-navy">{tr.limited}</strong></p>
        </div>
      </section>

      <div id="club-form" className="max-w-3xl mx-auto px-5 py-10 space-y-10">
        {err && (
          <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-4 flex gap-3 items-start" data-testid="club-error">
            <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" /><p className="text-sm text-red-700">{err}</p>
          </div>
        )}

        {/* Step 1: dates */}
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-navy mb-2">{tr.step1}</h2>
          <p className="text-sm text-ink/60 mb-4">{tr.step1Hint}</p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="club-dates">
            {sessions.map((s) => {
              const selected = d.session_dates.includes(s.date);
              const disabled = s.state !== "available";
              return (
                <button key={s.date} type="button" disabled={disabled} onClick={() => toggleDate(s.date)} data-testid={`club-date-${s.date}`}
                  className={`text-left rounded-2xl border-2 p-4 transition ${disabled ? "opacity-60 cursor-not-allowed border-slate-200 bg-white" : selected ? "border-brand bg-aqua/60" : "border-slate-200 bg-white hover:border-brand/50"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-navy capitalize">{fmtDate(s.date, lang)}</div>
                      <div className="text-xs text-ink/60 mt-0.5">{config?.time_label || "6:30–7:30 PM"} ET{ s.note ? ` · ${s.note}` : ""}</div>
                    </div>
                    {stateBadge(s)}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 2: your info */}
        <section className="space-y-5">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-navy">{tr.step2}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Lbl req>{tr.name}</Lbl><input data-testid="club-name" className={inp} value={d.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
            <div><Lbl req>{tr.phone}</Lbl><input type="tel" data-testid="club-phone" className={inp} value={d.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Lbl req>{tr.email}</Lbl><input type="email" data-testid="club-email" className={inp} value={d.email} onChange={(e) => set("email", e.target.value)} /></div>
          </div>
          <div>
            <Lbl req>{tr.level}</Lbl>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="club-level">
              {LEVELS.map((l) => (
                <label key={l.v} className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition ${d.spanish_level === l.v ? "border-brand bg-aqua/60" : "border-slate-200 bg-white"}`}>
                  <input type="radio" checked={d.spanish_level === l.v} onChange={() => set("spanish_level", l.v)} className="w-5 h-5 shrink-0 accent-[#26A6A1]" data-testid={`club-level-${l.v.replace(/\W+/g, "-").toLowerCase()}`} />
                  <span className="text-[15px] text-ink/80">{lang === "es" ? l.es : l.v}</span>
                </label>
              ))}
            </div>
          </div>
          <div><Lbl hint={tr.knowledgeHint}>{tr.knowledge}</Lbl><textarea rows={2} data-testid="club-knowledge" className={inp} value={d.spanish_knowledge} onChange={(e) => set("spanish_knowledge", e.target.value)} /></div>
          <div><Lbl req>{tr.goals}</Lbl><textarea rows={3} data-testid="club-goals" className={inp} value={d.goals} onChange={(e) => set("goals", e.target.value)} /></div>
          <div>
            <Lbl hint={tr.improveHint}>{tr.improve}</Lbl>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="club-improve">
              {IMPROVE.map((o) => (
                <label key={o.v} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${d.improve.includes(o.v) ? "border-brand bg-aqua/60" : "border-slate-200 bg-white"}`}>
                  <input type="checkbox" checked={d.improve.includes(o.v)} onChange={() => toggleImprove(o.v)} className="w-5 h-5 shrink-0 accent-[#26A6A1]" data-testid={`club-improve-${o.v.replace(/\W+/g, "-").toLowerCase()}`} />
                  <span className="text-sm text-ink/80">{lang === "es" ? o.es : o.v}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Lbl req>{tr.studied}</Lbl>
            <div className="flex flex-wrap gap-2" data-testid="club-studied">
              {STUDIED.map((o) => (
                <label key={o.v} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 cursor-pointer transition ${d.studied_before === o.v ? "border-brand bg-aqua/60" : "border-slate-200 bg-white"}`}>
                  <input type="radio" checked={d.studied_before === o.v} onChange={() => set("studied_before", o.v)} className="w-5 h-5 shrink-0 accent-[#26A6A1]" data-testid={`club-studied-${o.v.replace(/\W+/g, "-").toLowerCase()}`} />
                  <span className="text-[15px] text-ink/80">{lang === "es" ? o.es : o.v}</span>
                </label>
              ))}
            </div>
          </div>
          <div><Lbl hint={tr.notesHint}>{tr.notes}</Lbl><textarea rows={2} data-testid="club-notes" className={inp} value={d.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        </section>

        {/* Step 3: additional participants */}
        <section className="space-y-4">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-navy">{tr.step3}</h2>
          <Lbl req>{tr.addPersonQ}</Lbl>
          <div className="flex gap-2" data-testid="club-addperson">
            {["yes", "no"].map((v) => (
              <label key={v} className={`flex items-center gap-2.5 px-5 py-3 rounded-xl border-2 cursor-pointer transition ${addPerson === v ? "border-brand bg-aqua/60" : "border-slate-200 bg-white"}`}>
                <input type="radio" checked={addPerson === v} onChange={() => setAddPerson(v)} className="w-5 h-5 shrink-0 accent-[#26A6A1]" data-testid={`club-addperson-${v}`} />
                <span className="text-[15px] text-ink/80">{v === "yes" ? tr.yes : tr.no}</span>
              </label>
            ))}
          </div>
          {addPerson === "yes" && (
            <div className="space-y-4" data-testid="club-participants">
              {extras.map((x, i) => (
                <div key={i} className="rounded-2xl border-2 border-slate-200 bg-white p-5 space-y-4" data-testid={`club-participant-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-navy">{tr.participant} {i + 2}</span>
                    <button type="button" onClick={() => setExtras((p) => p.filter((_, j) => j !== i))} data-testid={`club-participant-${i}-remove`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-red-500 bg-red-50 rounded-full px-3 py-1.5"><Trash2 size={13} /> {tr.remove}</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2"><Lbl req>{tr.pName}</Lbl><input className={inp} data-testid={`club-participant-${i}-name`} value={x.name} onChange={(e) => setExtra(i, "name", e.target.value)} /></div>
                    <div>
                      <Lbl req>{tr.pLevel}</Lbl>
                      <select className={inp} data-testid={`club-participant-${i}-level`} value={x.spanish_level} onChange={(e) => setExtra(i, "spanish_level", e.target.value)}>
                        <option value="">—</option>
                        {LEVELS.map((l) => <option key={l.v} value={l.v}>{lang === "es" ? l.es : l.v}</option>)}
                      </select>
                    </div>
                    <div>
                      <Lbl req>{tr.pAge}</Lbl>
                      <select className={inp} data-testid={`club-participant-${i}-age`} value={x.age_category} onChange={(e) => setExtra(i, "age_category", e.target.value)}>
                        <option value="">—</option>
                        {AGE_CAT.map((a) => <option key={a.v} value={a.v}>{lang === "es" ? a.es : a.v}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2"><Lbl req>{tr.pGoals}</Lbl><textarea rows={2} className={inp} data-testid={`club-participant-${i}-goals`} value={x.goals} onChange={(e) => setExtra(i, "goals", e.target.value)} /></div>
                    <div><Lbl>{tr.pEmail}</Lbl><input type="email" className={inp} data-testid={`club-participant-${i}-email`} value={x.email} onChange={(e) => setExtra(i, "email", e.target.value)} /></div>
                    <div><Lbl>{tr.pPhone}</Lbl><input type="tel" className={inp} data-testid={`club-participant-${i}-phone`} value={x.phone} onChange={(e) => setExtra(i, "phone", e.target.value)} /></div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setExtras((p) => [...p, { ...emptyExtra }])} data-testid="club-add-participant"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-bold text-sm bg-navy text-white hover:bg-brand transition-colors"><Plus size={16} /> {tr.addParticipant}</button>
            </div>
          )}
        </section>

        {/* Step 4: summary + payment */}
        <section className="space-y-5">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-navy">{tr.step4}</h2>
          <div className="rounded-2xl bg-navy text-white p-6" data-testid="club-summary">
            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <div><div className="text-2xl font-extrabold" data-testid="club-summary-sessions">{d.session_dates.length}</div><div className="text-[11px] uppercase tracking-wide text-white/60">{tr.summarySessions}</div></div>
              <div><div className="text-2xl font-extrabold" data-testid="club-summary-people">{seats}</div><div className="text-[11px] uppercase tracking-wide text-white/60">{tr.summaryPeople}</div></div>
              <div><div className="text-2xl font-extrabold">${price}</div><div className="text-[11px] uppercase tracking-wide text-white/60">{tr.summaryPrice}</div></div>
            </div>
            <div className="border-t border-white/15 pt-4 text-center">
              <div className="text-sm text-white/70">{d.session_dates.length} × {seats} × ${price}</div>
              <div className="text-4xl font-extrabold text-gold" data-testid="club-summary-total">${total}</div>
              <div className="text-[11px] uppercase tracking-wide text-white/60 mt-1">{tr.summaryTotal}</div>
            </div>
          </div>
          <div>
            <Lbl req>{tr.payTitle}</Lbl>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="club-payment-methods">
              <label className={`rounded-2xl border-2 p-5 cursor-pointer transition ${d.payment_method === "venmo" ? "border-brand bg-aqua/60" : "border-slate-200 bg-white"}`} data-testid="club-pay-venmo">
                <div className="flex items-center gap-3 mb-2">
                  <input type="radio" checked={d.payment_method === "venmo"} onChange={() => set("payment_method", "venmo")} className="w-5 h-5 shrink-0 accent-[#26A6A1]" />
                  <span className="font-extrabold text-navy">{tr.payVenmo}</span>
                </div>
                <p className="text-sm text-ink/70">{config ? (lang === "es" ? config.payment_instructions_es : config.payment_instructions_en) : ""}</p>
              </label>
              {config?.payment_link ? (
                <label className={`rounded-2xl border-2 p-5 cursor-pointer transition ${d.payment_method === "online" ? "border-brand bg-aqua/60" : "border-slate-200 bg-white"}`} data-testid="club-pay-online">
                  <div className="flex items-center gap-3 mb-2">
                    <input type="radio" checked={d.payment_method === "online"} onChange={() => set("payment_method", "online")} className="w-5 h-5 shrink-0 accent-[#26A6A1]" />
                    <span className="font-extrabold text-navy">{tr.payOnline}</span>
                  </div>
                  <p className="text-sm text-ink/70">{lang === "es" ? config.online_instructions_es : config.online_instructions_en}</p>
                </label>
              ) : null}
            </div>
          </div>
        </section>

        {/* Policies */}
        <section className="rounded-2xl border-2 border-slate-200 bg-white p-6" data-testid="club-policies">
          <h2 className="font-heading text-lg font-extrabold text-navy mb-3 flex items-center gap-2"><ShieldCheck size={20} className="text-brand" /> {tr.policiesTitle}</h2>
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-ink/75 mb-4">
            {POLICIES[lang].map((p) => <li key={p}>{p}</li>)}
          </ul>
          {config && (lang === "es" ? config.refund_policy_es : config.refund_policy_en) ? (
            <p className="text-sm text-ink/75 mb-4 border-t border-slate-100 pt-3">{lang === "es" ? config.refund_policy_es : config.refund_policy_en}</p>
          ) : null}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={d.policies_ack} onChange={(e) => set("policies_ack", e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0 accent-[#26A6A1]" data-testid="club-policies-ack" />
            <span className="text-sm font-semibold text-navy">{tr.agree} <span className="text-red-500">*</span></span>
          </label>
        </section>

        <button type="button" onClick={submit} disabled={status === "sending"} data-testid="club-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl disabled:opacity-60 bg-navy hover:bg-brand transition-colors">
          {status === "sending" ? tr.submitting : <><Send size={18} /> {tr.submit} — ${total}</>}
        </button>
        <div className="text-center pb-8">
          <Link to="/" className="text-sm text-brand hover:underline inline-flex items-center gap-1.5"><Home size={14} /> {tr.returnHome}</Link>
        </div>
      </div>
    </div>
  );
}
